/**
 * @fileoverview Payment System Type Definitions
 *
 * This module defines the core TypeScript interfaces and enums used throughout
 * the payment management system. These types ensure type safety and provide
 * clear contracts between components and business logic.
 *
 * ## Type Categories:
 *
 * ### Payment Types:
 * - **PaymentType**: Enum distinguishing between regular and Lob payments
 * - **PayeeFormInputs**: Complete form data interface for payment creation/editing
 *
 * ### Association Types:
 * - **Association**: Links payments to reports and facilities for compliance tracking
 *
 * ## Business Rules Encoded in Types:
 * - Form inputs match GraphQL mutation requirements
 * - Association structure supports flexible report/facility linking
 * - Optional fields reflect business requirement variations
 * - Payment type enum drives conditional validation and UI behavior
 *
 * @author Payment Management Team
 * @since Phase 1 refactoring - type extraction and consolidation
 */

import {
  PaymentInvoiceInput,
  PaymentMethod,
  PaymentStatus,
  Payee,
} from "generated-graphql/graphql";

/**
 * Payment type enumeration
 *
 * Distinguishes between the two main payment processing flows:
 * - LOB_CHECK: Automated check printing/mailing via lob service
 * - RECORD_PAYMENT: Staff-recorded payment with manual entry
 *
 * This enum drives conditional validation, UI behavior, and submission logic.
 */
export enum PaymentType {
  /** Automated check payment via lob service - requires strict validation */
  LOB_CHECK = "lob",
  /** Staff-recorded payment - more flexible validation */
  RECORD_PAYMENT = "payment",
}

/**
 * Payment association interface
 *
 * Links payments to reports and facilities for proper compliance tracking
 * and cost allocation. Supports flexible associations where payments can
 * be split across multiple entities.
 *
 * ## Business Rules:
 * - Each association must have either reportId OR facilityId (not both)
 * - Amount represents the portion of total payment allocated to this entity
 * - Sum of all association amounts should equal total payment amount
 * - Associations enable detailed financial reporting and compliance tracking
 */
export interface Association {
  /** Portion of payment amount allocated to this association */
  amount: number;

  /** Portion of transaction fee allocated to this association */
  transactionFeeAmount?: number;

  /** Report ID for report-based associations */
  reportId?: string;

  /** Facility ID for facility-based associations */
  facilityId?: string;
}

/**
 * Report-specific association interface
 * Used when payment is associated with a specific report
 */
export interface ReportAssociation extends Association {
  reportId: string;
  facilityId?: never;
}

/**
 * Facility-specific association interface
 * Used when payment is associated with a specific facility
 */
export interface FacilityAssociation extends Association {
  facilityId: string;
  reportId?: never;
}

/**
 * Payment form input data interface
 *
 * Defines the complete structure of payment form data, encompassing all fields
 * needed for both payment types and all business scenarios.
 *
 * ## Field Categories:
 *
 * ### Core Payment Info:
 * - type, amount, paymentMethod, payeeType
 *
 * ### Payee Information:
 * - recipientName, companyName (for Lob payments)
 * - Address fields for check mailing
 *
 * ### Business Context:
 * - associations (reports/facilities), activityId, tenantId
 * - memo, invoiceNumber for reference tracking
 *
 * ### Documents:
 * - paymentInvoices (required for all payments), receiptDocument (optional)
 *
 * ### Status & Metadata:
 * - status, paidAt, billable flags
 * - comment for additional context
 *
 * ## Validation Notes:
 * - Required fields vary by payment type (enforced by validation schema)
 * - lob payments have stricter requirements than regular payments
 * - Some fields are only relevant for specific payment workflows
 */
export interface PayeeFormInputs {
  /** Payment processing type - determines validation and submission flow */
  type: PaymentType;

  /** Type of payee receiving the payment (LEPC, facility, etc.) */
  payeeType: Payee;

  /** Individual recipient name (required for lob payments) */
  recipientName: string;

  /** Company/organization name (optional for lob payments) */
  companyName: string;

  /** Street address line 1 (required for lob payments) */
  addressLine1: string;

  /** Street address line 2 (optional for lob payments) */
  addressLine2: string;

  /** City (required for lob payments) */
  city: string;

  /** State/province (required for lob payments) */
  state: string;

  /** ZIP/postal code (required for lob payments) */
  zip: string;

  /** Payment description/memo (character limited for lob) */
  memo: string;

  /** Invoice reference number */
  invoiceNumber: string;

  /** Payment method (must be CHECK for lob payments) */
  paymentMethod: PaymentMethod;

  /** Payment amount in dollars */
  amount: number;

  /** Transaction fee amount in dollars (allocated evenly across facilities) */
  transactionFee?: number;

  /** Date when payment was made (for record payments) */
  paidAt?: string;

  /** Array of invoice documents (required for all payments) */
  paymentInvoices?: PaymentInvoiceInput[];

  /** Optional receipt document */
  receiptDocument?: any;

  /** Report and facility associations for compliance tracking */
  associations: Association[];

  /** Activity context for payment categorization */
  activityId?: string;

  /** Current payment status */
  status?: PaymentStatus;

  /** Whether to requeue failed lob jobs */
  requeueJob?: boolean;

  /** Whether this payment is billable to the client */
  billable?: boolean;

  /** Additional comment/note about the payment */
  comment?: string;

  /** Organization ID for multi-tenant support */
  tenantId?: string;
}
